import { alpha, createTheme, Shadows } from "@mui/material";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { ds } from "./tokens";

type BrandColors = {
  primary: string;
  primaryHover: string;
  secondary: string;
};

// Ombre basse e morbide al posto di quelle Material: 1-4 superfici, 5-12 menu e popover, 13+ drawer e modali.
const buildShadows = (): Shadows => {
  const s: string[] = ["none"];
  for (let i = 1; i <= 24; i++) {
    if (i <= 2) s.push("0 1px 2px rgba(15,42,55,.06)");
    else if (i <= 4) s.push("0 4px 12px -6px rgba(15,42,55,.18)");
    else if (i <= 12) s.push(ds.shadowPop);
    else if (i <= 16) s.push(ds.shadowHover);
    else s.push(ds.shadowModal);
  }
  return s as Shadows;
};

const focusRing = (color: string) => `0 0 0 3px ${alpha(color, 0.18)}`;

export const buildTheme = ({ primary, primaryHover, secondary }: BrandColors) =>
  createTheme({
    palette: {
      mode: "light",
      primary: { main: primary, dark: primaryHover, light: alpha(primary, 0.85), contrastText: "#fff" },
      secondary: { main: secondary, contrastText: "#fff" },
      success: { main: ds.success, contrastText: "#fff" },
      warning: { main: ds.warning, dark: ds.warningStrong, contrastText: "#fff" },
      error: { main: ds.danger, dark: ds.dangerHover, contrastText: "#fff" },
      info: { main: ds.primary, contrastText: "#fff" },
      text: { primary: ds.ink, secondary: ds.muted, disabled: ds.subtle },
      divider: ds.border,
      background: { default: ds.canvas, paper: ds.surface },
      action: {
        hover: ds.hover,
        selected: ds.accentSoft,
        focus: alpha(primary, 0.12),
      },
    },
    shape: { borderRadius: 10 },
    shadows: buildShadows(),
    typography: {
      fontFamily: ds.font,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: { fontSize: "2.875rem", fontWeight: 600, lineHeight: 1.05, letterSpacing: "-0.03em" },
      h2: { fontSize: "2.125rem", fontWeight: 600, lineHeight: 1.1, letterSpacing: "-0.025em" },
      h3: { fontSize: "1.75rem", fontWeight: 600, lineHeight: 1.15, letterSpacing: "-0.02em" },
      h4: { fontSize: "1.3125rem", fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.015em" },
      h5: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.01em" },
      h6: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.35 },
      subtitle1: { fontSize: "1rem", fontWeight: 600 },
      subtitle2: { fontSize: "0.875rem", fontWeight: 600 },
      body1: { fontSize: "0.9375rem", lineHeight: 1.5 },
      body2: { fontSize: "0.875rem", lineHeight: 1.5 },
      caption: { fontSize: "0.75rem", color: ds.muted },
      overline: { fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.12em" },
      button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
    },

    components: {
      // ---- Pulsanti ----
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": { boxShadow: focusRing(primary) },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 12,
            fontSize: "0.875rem",
            minHeight: 40,
            padding: "0 16px",
            gap: 8,
            whiteSpace: "nowrap",
            transition: "background-color .25s, border-color .25s, color .25s, box-shadow .25s",
            "& .MuiButton-startIcon": { marginRight: 0, marginLeft: -2 },
            "& .MuiButton-endIcon": { marginLeft: 0, marginRight: -2 },
          },
          sizeSmall: { minHeight: 34, padding: "0 12px", fontSize: "0.8125rem", borderRadius: 10 },
          sizeLarge: { minHeight: 46, padding: "0 20px", fontSize: "0.9375rem" },
          contained: ({ ownerState, theme }) => {
            const key = ownerState.color && ownerState.color !== "inherit" ? ownerState.color : "primary";
            const c = theme.palette[key];
            return {
              "&:hover": { backgroundColor: c.dark, boxShadow: "none" },
              "&.Mui-disabled": { backgroundColor: c.main, color: c.contrastText, opacity: 0.45 },
            };
          },
          outlined: ({ ownerState }) =>
            ownerState.color === "error"
              ? {
                  borderColor: ds.dangerBorder,
                  color: ds.danger,
                  backgroundColor: ds.surface,
                  "&:hover": { borderColor: ds.danger, backgroundColor: ds.dangerSoft },
                }
              : {
                  borderColor: ds.borderStrong,
                  color: ds.ink,
                  backgroundColor: ds.surface,
                  "&:hover": { borderColor: primary, backgroundColor: ds.surface },
                  "&.Mui-disabled": { borderColor: ds.border, color: ds.subtle },
                },
          text: {
            padding: "0 10px",
            "&:hover": { backgroundColor: ds.accentSoft },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            color: ds.ink2,
            transition: "background-color .2s, color .2s",
            "&:hover": { backgroundColor: ds.hover },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderColor: ds.border,
            color: ds.ink2,
            "&.Mui-selected": { backgroundColor: ds.accentSoft, color: ds.accentSoftInk },
          },
        },
      },
      MuiLink: {
        defaultProps: { underline: "hover" },
        styleOverrides: { root: { fontWeight: 600 } },
      },

      // ---- Superfici e card ----
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          rounded: { borderRadius: 16 },
          outlined: { borderColor: ds.border },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${ds.border}`,
            borderRadius: 16,
            transition: "transform .45s cubic-bezier(.25,.1,.25,1), box-shadow .45s, border-color .3s",
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: "20px 24px 0" },
          title: { fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" },
          subheader: { fontSize: "0.875rem", color: ds.muted },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: "20px 24px", "&:last-child": { paddingBottom: 24 } } },
      },
      MuiCardActions: {
        styleOverrides: { root: { padding: "12px 24px 20px", gap: 8 } },
      },
      MuiAccordion: {
        defaultProps: { elevation: 0, disableGutters: true },
        styleOverrides: {
          root: {
            border: `1px solid ${ds.border}`,
            borderRadius: 16,
            "&:before": { display: "none" },
            "&:not(:last-of-type)": { marginBottom: 12 },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: { root: { padding: "0 20px", minHeight: 56 }, content: { fontWeight: 600 } },
      },
      MuiAccordionDetails: {
        styleOverrides: { root: { padding: "0 20px 20px" } },
      },

      // ---- Popup e modali ----
      MuiBackdrop: {
        styleOverrides: {
          root: ({ ownerState }) =>
            ownerState.invisible ? {} : { backgroundColor: "rgba(15,42,55,.42)", backdropFilter: "blur(2px)" },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 20, boxShadow: ds.shadowModal },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "20px 24px 14px",
            fontSize: "1.3125rem",
            fontWeight: 600,
            letterSpacing: "-0.015em",
            color: ds.ink,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: "8px 24px 20px" },
          dividers: { borderColor: ds.divider, padding: "20px 24px" },
        },
      },
      MuiDialogContentText: {
        styleOverrides: { root: { color: ds.muted, fontSize: "0.9375rem" } },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "14px 24px",
            gap: 8,
            backgroundColor: ds.surface3,
            borderTop: `1px solid ${ds.divider}`,
            "& > :not(:first-of-type)": { marginLeft: 0 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: ds.ink,
            color: "#fff",
            fontSize: "0.78125rem",
            fontWeight: 500,
            padding: "6px 10px",
            borderRadius: 8,
          },
          arrow: { color: ds.ink },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundColor: ds.ink,
            color: "#fff",
            borderRadius: 12,
            fontSize: "0.84375rem",
            boxShadow: ds.shadowHover,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, fontSize: "0.875rem", fontWeight: 500, alignItems: "center" },
          standardSuccess: { backgroundColor: ds.successSoft, color: ds.successInk },
          standardInfo: { backgroundColor: ds.accentSoft, color: ds.accentSoftInk },
          standardWarning: { backgroundColor: ds.warningSoft, color: ds.warningInk },
          standardError: { backgroundColor: ds.dangerSoft, color: ds.danger, border: `1px solid ${ds.dangerBorder}` },
          filledSuccess: { backgroundColor: ds.success },
          filledInfo: { backgroundColor: ds.ink },
          filledWarning: { backgroundColor: ds.warningStrong },
          filledError: { backgroundColor: ds.danger },
          outlined: { backgroundColor: ds.surface },
        },
      },

      // ---- Menu, liste e navigazione ----
      MuiPopover: {
        styleOverrides: {
          paper: { borderRadius: 12, border: `1px solid ${ds.border}`, boxShadow: ds.shadowPop },
        },
      },
      MuiMenu: {
        styleOverrides: {
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            minHeight: 38,
            padding: "8px 10px",
            borderRadius: 8,
            fontSize: "0.875rem",
            fontWeight: 500,
            color: ds.ink,
            "&:hover": { backgroundColor: ds.hover },
            "&.Mui-selected": { backgroundColor: ds.accentSoft, fontWeight: 600 },
            "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": { backgroundColor: "#DDEEF7" },
            "& .MuiListItemIcon-root": { minWidth: 30, color: primary },
          },
        },
      },
      MuiList: {
        styleOverrides: { root: { backgroundColor: "transparent" } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            minHeight: 38,
            color: ds.ink2,
            transition: "background-color .2s, color .2s",
            "&:hover": { backgroundColor: ds.hover, color: ds.ink },
            "&.Mui-selected": {
              backgroundColor: ds.accentSoft,
              color: ds.ink,
              "& .MuiListItemIcon-root": { color: primary },
              "& .MuiListItemText-primary": { fontWeight: 600 },
            },
            "&.Mui-selected:hover": { backgroundColor: "#DDEEF7" },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: { root: { minWidth: 34, color: ds.icon } },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: { fontSize: "0.90625rem", fontWeight: 500 },
          secondary: { fontSize: "0.8125rem", color: ds.muted },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: ds.muted,
            lineHeight: "32px",
            backgroundColor: "transparent",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: ds.surface, borderRight: `1px solid ${ds.border}` },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { boxShadow: "none", borderBottom: `1px solid ${ds.border}` },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44, borderBottom: `1px solid ${ds.border}` },
          indicator: { height: 2, borderRadius: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 44,
            minWidth: "auto",
            padding: "0 14px",
            textTransform: "none",
            fontSize: "0.9375rem",
            fontWeight: 500,
            color: ds.muted,
            "&:hover": { color: ds.ink },
            "&.Mui-selected": { color: ds.ink, fontWeight: 600 },
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: { fontSize: "0.875rem", color: ds.muted },
          separator: { color: ds.subtle },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            borderRadius: 9,
            fontWeight: 500,
            "&.Mui-selected": { backgroundColor: ds.accentSoft, color: ds.accentSoftInk, fontWeight: 600 },
          },
        },
      },

      // ---- Campi ----
      MuiInputBase: {
        styleOverrides: {
          root: { fontSize: "0.9375rem", color: ds.ink },
          input: { "&::placeholder": { color: ds.subtle, opacity: 1 } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 11,
            backgroundColor: ds.surface,
            transition: "box-shadow .25s",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: ds.border, transition: "border-color .25s" },
            "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderColor: ds.borderStrong,
            },
            "&.Mui-focused": { boxShadow: focusRing(primary) },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: primary, borderWidth: 1 },
            "&.Mui-error": { backgroundColor: ds.dangerSoft },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ds.danger },
            "&.Mui-error.Mui-focused": { boxShadow: focusRing(ds.danger) },
            "&.Mui-disabled": { backgroundColor: ds.surface2 },
            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: ds.border },
          },
          input: { padding: "11px 14px" },
          inputSizeSmall: { padding: "8.5px 12px" },
          multiline: { padding: "11px 14px" },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            fontSize: "0.9375rem",
            color: ds.muted,
            "&.Mui-focused": { color: primary },
            "&.Mui-error": { color: ds.danger },
            ...(ownerState.variant === "outlined" &&
              !ownerState.shrink &&
              ownerState.size !== "small" && { transform: "translate(14px, 11px) scale(1)" }),
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: { color: ds.ink2, fontWeight: 500, "&.Mui-focused": { color: primary } },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { fontSize: "0.78125rem", marginLeft: 2, marginTop: 6 } },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: ({ ownerState }) => (ownerState.size === "small" ? {} : { paddingTop: 4, paddingBottom: 4 }),
          paper: { borderRadius: 12, border: `1px solid ${ds.border}`, boxShadow: ds.shadowPop },
          listbox: {
            padding: 6,
            "& .MuiAutocomplete-option": { borderRadius: 8, minHeight: 38, fontSize: "0.875rem" },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: { root: { color: "#A5B6BE", borderRadius: 8 } },
      },
      MuiRadio: {
        styleOverrides: { root: { color: "#A5B6BE" } },
      },
      MuiSwitch: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const small = ownerState.size === "small";
            return {
              width: small ? 36 : 44,
              height: small ? 20 : 26,
              padding: 0,
              margin: 8,
              overflow: "visible",
              "& .MuiSwitch-switchBase": {
                padding: 3,
                color: "#fff",
                "&:hover": { backgroundColor: "transparent" },
                "&.Mui-checked": {
                  transform: `translateX(${small ? 16 : 18}px)`,
                  color: "#fff",
                  "& + .MuiSwitch-track": { backgroundColor: primary, opacity: 1 },
                },
                "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.45 },
              },
              "& .MuiSwitch-thumb": {
                width: small ? 14 : 20,
                height: small ? 14 : 20,
                boxShadow: "0 1px 3px rgba(0,0,0,.2)",
              },
              "& .MuiSwitch-track": { borderRadius: 999, backgroundColor: ds.borderStrong, opacity: 1 },
            };
          },
        },
      },
      MuiSlider: {
        styleOverrides: { rail: { backgroundColor: ds.border, opacity: 1 } },
      },

      // ---- Tag, badge, avatar ----
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600, fontSize: "0.8125rem", height: 30 },
          sizeSmall: { borderRadius: 6, height: 24, fontSize: "0.75rem" },
          filled: ({ ownerState }) =>
            !ownerState.color || ownerState.color === "default"
              ? { backgroundColor: ds.accentSoft, color: ds.accentSoftInk }
              : {},
          outlined: ({ ownerState }) =>
            !ownerState.color || ownerState.color === "default" ? { borderColor: ds.border, color: ds.ink2 } : {},
          deleteIcon: { fontSize: 18 },
        },
      },
      MuiBadge: {
        styleOverrides: { badge: { fontWeight: 600 } },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 600, fontSize: "0.875rem" },
          colorDefault: { backgroundColor: ds.ink, color: "#fff" },
          rounded: { borderRadius: 10 },
        },
        variants: [
          {
            props: { variant: "square" },
            style: { background: ds.ink, color: "#fff", borderRadius: 10 },
          },
        ],
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: ds.border },
          withChildren: {
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: ds.muted,
            "&::before": { width: 28, flex: "none", borderTop: `1px solid ${primary}` },
            "&::after": { width: "auto", flex: 1, borderTop: `1px solid ${ds.border}` },
          },
        },
      },

      // ---- Avanzamento ----
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 4, borderRadius: 4, backgroundColor: ds.border },
          bar: { borderRadius: 4 },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: { color: ds.borderStrong, "&.Mui-active, &.Mui-completed": { color: primary } },
          text: { fontWeight: 600 },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontSize: "0.875rem",
            color: ds.muted,
            "&.Mui-active, &.Mui-completed": { color: ds.ink, fontWeight: 600 },
          },
        },
      },

      // ---- Tabelle ----
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: ds.divider, fontSize: "0.875rem", color: ds.ink },
          head: { fontSize: "0.8125rem", fontWeight: 600, color: ds.muted, backgroundColor: ds.surface3 },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            border: `1px solid ${ds.border}`,
            borderRadius: 16,
            boxShadow: "none",
            backgroundColor: ds.surface,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { "&.MuiTableRow-hover:hover": { backgroundColor: ds.surface3 } },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            borderColor: ds.border,
            borderRadius: 16,
            backgroundColor: ds.surface,
            fontSize: "0.875rem",
            color: ds.ink,
            "& .MuiDataGrid-columnHeaders": { backgroundColor: ds.surface3, borderBottomColor: ds.divider },
            "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: "0.8125rem", color: ds.muted },
            "& .MuiDataGrid-columnHeader": { fontSize: "0.8125rem" },
            "& .MuiDataGrid-columnSeparator": { color: ds.border },
            "& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": { color: ds.muted },
            "& .MuiDataGrid-cell": { borderBottomColor: ds.divider },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-row:hover": { backgroundColor: ds.surface3 },
            "& .MuiDataGrid-row.Mui-selected": { backgroundColor: ds.accentSoft },
            "& .MuiDataGrid-footerContainer": { borderTopColor: ds.divider },
          },
        },
      },
    },
  });
